import { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { useAuth } from "../context/AuthContext";
import { Lock, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";

function formatErr(detail) {
    if (detail == null) return "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    if (detail?.msg) return detail.msg;
    return String(detail);
}

export default function AdminPassword() {
    const { logout, user } = useAuth();
    const navigate = useNavigate();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [saving, setSaving] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (next.length < 8) return toast.error("New password must be at least 8 characters.");
        if (next !== confirm) return toast.error("Passwords do not match.");
        if (current === next) return toast.error("New password must differ from the current one.");
        setSaving(true);
        try {
            await api.post("/auth/change-password", { current_password: current, new_password: next });
            toast.success("Password changed. Please sign in again.");
            await logout();
            navigate("/admin/login");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminLayout>
            <div className="max-w-xl">
                <div className="text-[10px] uppercase tracking-[0.4em] text-meraki-gold mb-3">Security</div>
                <h1 className="font-display text-4xl md:text-5xl uppercase text-meraki-text leading-tight mb-3">Change Password</h1>
                <p className="text-meraki-muted text-sm mb-10">
                    Signed in as <span className="text-meraki-text">{user?.email}</span>. After changing your password, you will be signed out and asked to log in again.
                </p>

                <form onSubmit={onSubmit} className="bg-meraki-surface border border-meraki-gold/20 p-8 space-y-5">
                    <FieldRow label="Current password" testid="pw-current" value={current} onChange={setCurrent} />
                    <FieldRow label="New password (min 8 chars)" testid="pw-new" value={next} onChange={setNext} />
                    <FieldRow label="Confirm new password" testid="pw-confirm" value={confirm} onChange={setConfirm} />

                    <button
                        type="submit"
                        disabled={saving || !current || !next || !confirm}
                        data-testid="pw-submit"
                        className="w-full mt-4 bg-meraki-gold text-black py-4 text-xs uppercase tracking-[0.3em] font-bold hover:bg-meraki-gold-hover transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        <Save className="w-3 h-3" /> {saving ? "Updating..." : "Update password"}
                    </button>
                </form>
            </div>
        </AdminLayout>
    );
}

function FieldRow({ label, testid, value, onChange }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-meraki-muted mb-2">{label}</label>
            <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-meraki-muted" />
                <input
                    type="password"
                    required
                    data-testid={testid}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full bg-black border border-meraki-gold/20 px-10 py-3 text-meraki-text focus:outline-none focus:border-meraki-gold"
                />
            </div>
        </div>
    );
}
